// UserProfileProfessional.tsx
import React from "react";
import { TeamMember } from "../../../types/admin/TeamTypes";
import { Input, Select, Row, Col, Typography, Space, Tag } from "antd";
import { ContrastTag } from "../../../components/shared";
// Direct path, deliberately NOT via the shared barrel — see the note in
// components/shared/index.ts.
import PractitionerColorPicker from "../../../components/shared/PractitionerColorPicker";

const { TextArea } = Input;
const { Text } = Typography;

interface UserProfileProfessionalProps {
	teamMember: TeamMember;
	isEditing: boolean;
	setTeamMember: React.Dispatch<React.SetStateAction<TeamMember | null>>;
}

const UserProfileProfessional: React.FC<UserProfileProfessionalProps> = ({ teamMember, isEditing, setTeamMember }) => {
	const handleInputChange = (field: string, value: string | number) => {
		setTeamMember((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				team_member_info: {
					...prev.team_member_info,
					[field]: value,
				},
			};
		});
	};

	const handleArrayChange = (field: string, value: string[]) => {
		setTeamMember((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				team_member_info: {
					...prev.team_member_info,
					[field]: value,
				},
			};
		});
	};

	const handleColorChange = (hex: string | null) => {
		setTeamMember((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				team_member_info: {
					...prev.team_member_info,
					color: hex,
				},
			};
		});
	};

	const handleNestedChange = (parent: string, field: string, value: string) => {
		setTeamMember((prev) => {
			if (!prev) return prev;
			const prevNested = (prev.team_member_info as any)?.[parent] || {};
			return {
				...prev,
				team_member_info: {
					...prev.team_member_info,
					[parent]: { ...prevNested, [field]: value },
				},
			};
		});
	};

	const sections = [
		{
			title: "Medical Credentials",
			fields: [
				{ key: "medical_license_number", label: "Medical License Number", type: "text" },
				{ key: "npi_number", label: "NPI Number", type: "text" },
				{ key: "dea_number", label: "DEA Number", type: "text" },
				{ key: "taxonomy_code", label: "Taxonomy Code", type: "text" },
			],
		},
		{
			title: "Professional Details",
			fields: [
				{ key: "years_of_experience", label: "Years of Experience", type: "number" },
				{ key: "supervisor_id", label: "Supervisor ID", type: "text" },
			],
		},
		{
			title: "Specialization",
			arrayFields: [
				{ key: "specialties", label: "Specialties" },
				{ key: "qualifications", label: "Qualifications" },
				{ key: "certifications", label: "Certifications" },
				{ key: "professional_memberships", label: "Professional Memberships" },
				{ key: "languages_spoken", label: "Languages Spoken" },
			],
		},
		{
			title: "Biography",
			textareaField: { key: "biography", label: "Professional Biography" },
		},
		{
			title: "Insurance Information",
			nested: "malpractice_insurance_details",
			fields: [
				{ key: "provider", label: "Insurance Provider" },
				{ key: "policy_number", label: "Policy Number" },
				{ key: "coverage_amount", label: "Coverage Amount" },
			],
		},
		{
			title: "Billing Information",
			nested: "billing_information",
			fields: [
				{ key: "account_number", label: "Account Number" },
				{ key: "billing_address", label: "Billing Address" },
			],
		},
	];

	return (
		<Space direction="vertical" size="large" style={{ width: "100%" }}>
			{/* Calendar Colour — an override on top of the auto-allocated colour (S1-S3); the
			    practitioner may clear it to fall back to that automatic colour. */}
			<div>
				<Text strong style={{ fontSize: 13, display: "block", marginBottom: 12 }}>Calendar Colour</Text>
				{isEditing ? (
					<PractitionerColorPicker
						value={teamMember.team_member_info?.color ?? null}
						onChange={handleColorChange}
						currentUserId={teamMember.id}
					/>
				) : teamMember.team_member_info?.color ? (
					<Space align="center" size="small">
						<span
							style={{
								width: 16,
								height: 16,
								borderRadius: "50%",
								backgroundColor: teamMember.team_member_info.color,
								display: "inline-block",
								flexShrink: 0,
							}}
						/>
						<Text style={{ fontSize: 13 }}>{teamMember.team_member_info.color}</Text>
					</Space>
				) : (
					<Text type="secondary" style={{ fontSize: 13 }}>—</Text>
				)}
			</div>

			{sections.map((section, idx) => (
				<div key={idx}>
					<Text strong style={{ fontSize: 13, display: "block", marginBottom: 12 }}>{section.title}</Text>

					{section.fields && (
						<Row gutter={[16, 16]}>
							{section.fields.map((field) => (
								<Col key={field.key} xs={24} md={12}>
									<div>
										<Text type="secondary" style={{ fontSize: 11 }}>{field.label}</Text>
										{isEditing ? (
											<Input
												value={section.nested ? (teamMember.team_member_info as any)?.[section.nested]?.[field.key] || "" : String((teamMember.team_member_info as any)?.[field.key] || "")}
												onChange={(e) =>
													section.nested ? handleNestedChange(section.nested, field.key, e.target.value) : handleInputChange(field.key, "type" in field && field.type === "number" ? parseInt(e.target.value) || 0 : e.target.value)
												}
												type={"type" in field ? field.type : "text"}
												style={{ marginTop: 4 }}
											/>
										) : (
											<Text style={{ fontSize: 13, display: "block", marginTop: 4 }}>
												{section.nested ? (teamMember.team_member_info as any)?.[section.nested]?.[field.key] || "—" : (teamMember.team_member_info as any)?.[field.key] || "—"}
											</Text>
										)}
									</div>
								</Col>
							))}
						</Row>
					)}

					{section.arrayFields && (
						<Row gutter={[16, 16]}>
							{section.arrayFields.map((field) => (
								<Col key={field.key} xs={24}>
									<div>
										<Text type="secondary" style={{ fontSize: 11 }}>{field.label}</Text>
										{isEditing ? (
											<Select
												mode="tags"
												value={(teamMember.team_member_info as any)?.[field.key] || []}
												onChange={(value) => handleArrayChange(field.key, value)}
												placeholder={`Add ${field.label.toLowerCase()}`}
												style={{ width: "100%", marginTop: 4 }}
											/>
										) : (
											<Space wrap size="small" style={{ marginTop: 8 }}>
												{((teamMember.team_member_info as any)?.[field.key] || []).length > 0 ? (
													((teamMember.team_member_info as any)?.[field.key] || []).map((item: string, i: number) => (
														<ContrastTag key={i} background="var(--mg-primary)">{item}</ContrastTag>
													))
												) : (
													<Text type="secondary" style={{ fontSize: 13 }}>
														—
													</Text>
												)}
											</Space>
										)}
									</div>
								</Col>
							))}
						</Row>
					)}

					{section.textareaField && (
						<div>
							<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 8 }}>{section.textareaField.label}</Text>
							{isEditing ? (
								<TextArea
									value={(teamMember.team_member_info as any)?.[section.textareaField.key] || ""}
									onChange={(e) => handleInputChange(section.textareaField.key, e.target.value)}
									rows={4}
									placeholder="Enter your professional biography..."
								/>
							) : (
								<div
									style={{
										padding: 12,
										borderRadius: 6,
										fontSize: 13,
										lineHeight: 1.6,
										border: "1px solid var(--mg-border)",
										backgroundColor: "var(--mg-bg-subtle)",
									}}
								>
									{(teamMember.team_member_info as any)?.[section.textareaField.key] || "—"}
								</div>
							)}
						</div>
					)}
				</div>
			))}
		</Space>
	);
};

export default UserProfileProfessional;
